/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import styled from "styled-components";

import { Layer } from "@vienna/common";

import { DragCanvasSession } from "src/classes/plate/Session";
import { DragMode } from "src/classes/plate/Session/session.types";
import { PositionUtils } from "src/classes/utilities/PositionUtils";
import { StyleConstants } from "src/classes/utilities/StyleConstants";
interface Props {
  session: DragCanvasSession;
}

export function DragRect({ session }: Props) {
  const mode = session.observe((state) => state.mode);
  const anchorY = session.observe((state) => state.anchor.y); // must observe individually to trigger rerender (objects have reference equality)
  const anchorX = session.observe((state) => state.anchor.x);
  const focusY = session.observe((state) => state.focus.y);
  const focusX = session.observe((state) => state.focus.x);
  const position = PositionUtils.getCSSPositionFromPoints({ x: anchorX, y: anchorY }, { x: focusX, y: focusY });
  const { top, left, height, width } = position;
  const bigEnough = height >= StyleConstants.minBlockHeight && width >= StyleConstants.minBlockWidth;

  return <StyledDragRect style={{ top, left, height, width }} mode={mode} bigEnough={bigEnough} />;
}

const StyledDragRect = styled.div<{ mode: DragMode; bigEnough: boolean }>`
  position: relative;
  z-index: ${Layer.WIN_LAYER};
  background-color: ${({ mode, theme, bigEnough }) => {
    if (mode === DragMode.Multiselect) {
      return theme.canvas.select;
    }
    return bigEnough ? theme.canvas.create : theme.canvas.dragProhibited;
  }};
`;
